import { DateTime } from "luxon";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/shadcn/card";
import { AlertTriangle, ArrowDown, Clock } from "lucide-react";
import { PublicQuote, QuoteWSUpdate, QuoteUnit, QuoteSide } from "@/types/quotes";
import {
  formatAssetAmount,
  formatDateTime,
} from "@/lib/utils";

interface ExpiredQuoteProps {
  quote: PublicQuote;
  quotePrices: QuoteWSUpdate | null;
}

export function ExpiredQuoteComponent({
  quote,
  quotePrices,
}: ExpiredQuoteProps) {
  const expirationTime = DateTime.fromISO(quote.expiration, {
    zone: "America/Mexico_City",
  });

  // Use the new asset model and from/to logic
  const isBuy = quote.side === "buy";
  // For BUY: from = counter, to = base
  // For SELL: from = base, to = counter
  const fromAsset = isBuy ? quote.counter : quote.base;
  const toAsset = isBuy ? quote.base : quote.counter;
  const fromAmount = isBuy ? quotePrices?.total_from : quotePrices?.total_from;
  const toAmount = isBuy ? quotePrices?.total_to : quotePrices?.total_to;

  const lastPrice = quote.acceptance?.final_price || quote.latest_price;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B0F1A] p-6">
      {/* BAX OTC Logo */}
      <div className="flex items-center mb-2">
        <img src="/logo_bax.png" alt="BAX Logo" className="h-8 md:h-10 mr-3" />
        <span className="font-[165] text-gray-400 tracking-[0.3em] text-[2.2rem] md:text-[2.8rem] mt-[0.2rem]">
          OTC
        </span>
      </div>

      {/* Expired Message Card */}
      <div className="w-full max-w-lg mb-6">
        <Card className="bg-red-900/40 backdrop-blur-xl shadow-2xl border border-red-500/30 rounded-2xl md:rounded-3xl overflow-hidden">
          <CardContent className="pt-4 md:pt-6 pb-3 md:pb-4">
            <div className="flex flex-col items-center text-center">
              <AlertTriangle className="h-12 w-12 md:h-16 md:w-16 text-red-400 mb-3 md:mb-4" />
              <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                La cotización ha expirado
              </h2>
              <p className="text-sm md:text-base text-gray-300">
                Esta cotización ya no es válida.
                <br /> Por favor, contacte a nuestro equipo para una nueva
                cotización.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Quote Card (Dimmed) */}
      <Card className="w-full max-w-lg bg-[#111827]/90 backdrop-blur-xl shadow-2xl rounded-2xl md:rounded-3xl overflow-hidden opacity-60">
        <CardHeader className="px-4 md:px-8 pt-6 md:pt-8 pb-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg md:text-2xl font-medium text-white">
              Cotización OTC
            </CardTitle>
            <div className="flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-red-900/40 rounded-full">
              <AlertTriangle className="h-3 w-3 md:h-4 md:w-4 text-red-400" />
              <span className="text-red-400 text-xs md:text-sm font-medium">Expirada</span>
            </div>
          </div>
          <p className="text-sm md:text-md text-gray-400 mt-1">
            para{" "}
            <span className="font-semibold text-gray-200">
              {quote.client_name}
            </span>
          </p>
        </CardHeader>
        {/* Gray out the card content */}
        <CardContent className="px-4 md:px-8 pt-4 md:pt-6 opacity-60">
          <div className="space-y-4">
            {/* Exchange Box Container */}
            <div className="bg-[#0D1526] rounded-xl md:rounded-3xl px-3 md:px-6 py-3 md:py-4 space-y-1 border border-gray-800">
              {/* Entregarás Section */}
              <div className="space-y-1.5">
                <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                  Entregarás:
                </span>
                {/* From Asset Box */}
                <div className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
                    <div className="flex items-center space-x-2 md:space-x-3">
                      <div className="relative">
                        {fromAsset?.image_url && (
                          <img
                            src={fromAsset.image_url}
                            alt={fromAsset.name}
                            className="w-5 h-5 md:w-6 md:h-6 rounded-full"
                          />
                        )}
                      </div>
                      <span className="text-base md:text-xl font-medium text-white">
                        {fromAsset?.code}
                      </span>
                    </div>
                    <div className="text-lg md:text-2xl font-medium text-[#FF6B00] text-center">
                      {formatAssetAmount(
                        fromAmount,
                        fromAsset
                      )}
                      {" "}
                      {fromAsset?.code}
                    </div>
                  </div>
                </div>
              </div>
              {/* Arrow Divider */}
              <div className="flex items-center justify-center pt-2 md:pt-3">
                <div className="h-[1px] flex-1 bg-gray-800"></div>
                  <div className="bg-[#080B12] p-1.5 md:p-2 rounded-full border border-gray-800/50">
                    <ArrowDown className="h-3 w-3 md:h-4 md:w-4 text-gray-500" />
                  </div>
                <div className="h-[1px] flex-1 bg-gray-800"></div>
              </div>
              {/* Recibirás Section */}
              <div className="space-y-1.5 pb-1">
                <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                  Recibirás:
                </span>
                {/* To Asset Box */}
                <div className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
                    <div className="flex items-center space-x-2 md:space-x-3">
                      <div className="relative">
                        {toAsset?.image_url && (
                          <img
                            src={toAsset.image_url}
                            alt={toAsset.name}
                            className="w-5 h-5 md:w-6 md:h-6 rounded-full"
                          />
                        )}
                      </div>
                      <span className="text-base md:text-xl font-medium text-white">
                        {toAsset?.code}
                      </span>
                    </div>
                    <div className="text-lg md:text-2xl font-medium text-[#00E676] text-center">
                      {formatAssetAmount(toAmount, toAsset)}
                      {" "}
                      {toAsset?.code}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Price Section */}
            <div className="space-y-1.5">
              <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                Precio:
              </span>
              {/* Price Box */}
              <div className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50">
                <div className="flex justify-center items-center">
                  {
                    lastPrice ? (
                      <span className="text-base md:text-xl font-semibold tracking-wide text-[#FFD700]">
                        1 {quote.base.code} ={" "}
                        {formatAssetAmount(
                          lastPrice.toString(),
                          quote.counter,
                        )}
                        {" "}
                        {quote.counter.code}
                      </span>
                    ) : (
                      <span className="text-base md:text-xl font-semibold tracking-wide text-red-500">
                        ---
                      </span>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="justify-center px-4 md:px-8 pb-4 pt-2 md:pt-3">
          <div className="flex text-gray-400 text-xs md:text-sm">
            <Clock className="h-3 w-3 md:h-4 md:w-4 mr-2" />
            Cotización expiró el {formatDateTime(quote.expiration)}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
